import { Box, Divider, Stack } from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { InformationOutlinedIcon, theme } from '@synergy/theme';
import { PoolMath, getConditionTotals, getCurrencySymbol } from '@synergy/utils';
import { PoolTooltip, StyledSumTableRow } from '@synergy/components-next';
import { ReactElement, useMemo } from 'react';
import { getUnitTranslationId } from '@synergy/models';
import { useFormikContext } from 'formik';

import { BonusSettlementDetailFormValues } from '../../models/bonus-settlement-detail-form-values';
import { bestLocaleSelector } from '../../../../common/selectors/locale-selectors';
import { bonusSettlementDetailSelector } from '../store/bonus-settlements-detail-selector';
import { useAppSelector } from '../../../../store/store';

export const BonusSettlementDetailSumTable = ({ isModal = false }: { isModal?: boolean }): ReactElement => {
    const bonusSettlementDetail = useAppSelector(bonusSettlementDetailSelector);
    const intl = useIntl();
    const locale = useAppSelector(bestLocaleSelector);
    const defaultCurrency = useAppSelector(state => state.settings.currency);
    const { values } = useFormikContext<BonusSettlementDetailFormValues>();
    const totalNet = useMemo(() => {
        return values.customerBonusSettlements.reduce((acc, customerBonusSettlement) => {
            return acc + (customerBonusSettlement.actualBonus ?? 0);
        }, 0);
    }, [values.customerBonusSettlements]);

    // The endpoint sends the rate as bonusTaxPercentage; reading a tax rate
    // object it does not send showed 0 % and a total without VAT (#2268).
    const bonusTaxPercentage = bonusSettlementDetail?.bonusTaxPercentage ?? 0;
    // Rounded to the cent, as the admin does.
    const calculatedTax = PoolMath.round(totalNet * (bonusTaxPercentage / 100));
    const total = totalNet + calculatedTax;

    return (
        <Box>
            <Stack divider={<Divider />}>
                {bonusSettlementDetail?.bonusConditions?.map(condition => {
                    const { mode, currency, unit } = condition.bonusCondition;
                    const { value: conditionValueSum, bonus: conditionBonus } = getConditionTotals(
                        condition,
                        values.customerBonusSettlements
                    );

                    return (
                        <StyledSumTableRow key={condition.bonusCondition.id}>
                            <Stack sx={{ textAlign: 'left' }}>
                                <FormattedMessage
                                    id="app.bonusSettlements.supplierDetail.detail.condition"
                                    values={{
                                        currency: getCurrencySymbol(currency || defaultCurrency, locale),
                                        value: intl.formatNumber(conditionValueSum, {
                                            minimumFractionDigits: mode === 'amount' ? 0 : 2,
                                            maximumFractionDigits: 2,
                                        }),
                                        mode,
                                        unit: unit && intl.formatMessage({ id: getUnitTranslationId(unit) }),
                                    }}
                                />
                                <Stack direction="row" alignItems="center" gap={0.5} color="grey.800">
                                    <FormattedMessage
                                        id={`app.bonusSettlementSupplier.bonusSettlementCondition.${mode}`}
                                        values={{
                                            mode,
                                            quantity: condition.bonusCondition.quantity || 1,
                                            percentage: intl.formatNumber(
                                                (condition.bonusCondition.percentage ?? 0) * 100,
                                                {
                                                    maximumFractionDigits: 2,
                                                }
                                            ),
                                            price: intl.formatNumber(condition.bonusCondition.price ?? 0, {
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 2,
                                            }),
                                            currency: getCurrencySymbol(currency || defaultCurrency, locale),
                                            unit:
                                                unit &&
                                                intl.formatMessage({
                                                    id: getUnitTranslationId(unit),
                                                }),
                                        }}
                                    />
                                    {condition.bonusCondition.title && (
                                        <PoolTooltip title={condition.bonusCondition.title}>
                                            <InformationOutlinedIcon
                                                htmlColor={theme.palette.grey[800]}
                                                sx={{ fontSize: 16 }}
                                            />
                                        </PoolTooltip>
                                    )}
                                </Stack>
                            </Stack>

                            <FormattedNumber
                                value={conditionBonus}
                                style="currency"
                                currency={currency || defaultCurrency}
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </StyledSumTableRow>
                    );
                })}
            </Stack>

            <Divider sx={isModal ? {} : { backgroundColor: theme => theme.palette.text.primary }} />

            <Stack divider={<Divider />}>
                <StyledSumTableRow sx={{ fontWeight: 600 }}>
                    <Box>
                        <FormattedMessage id="app.bonusSettlements.supplierDetail.totalNet" />
                    </Box>
                    <Box>
                        <FormattedNumber
                            value={PoolMath.round(totalNet)}
                            style="currency"
                            currency={defaultCurrency}
                            maximumFractionDigits={2}
                        />
                    </Box>
                </StyledSumTableRow>

                <StyledSumTableRow>
                    <Box>
                        <FormattedMessage
                            id="app.bonusSettlements.supplierDetail.tax"
                            values={{ value: bonusTaxPercentage }}
                        />
                    </Box>
                    <Box>
                        <FormattedNumber
                            value={PoolMath.round(calculatedTax)}
                            style="currency"
                            currency={defaultCurrency}
                            minimumFractionDigits={2}
                            maximumFractionDigits={2}
                        />
                    </Box>
                </StyledSumTableRow>
            </Stack>

            {!isModal && <Divider sx={{ backgroundColor: theme => theme.palette.text.primary }} />}

            <StyledSumTableRow isModal={isModal} sx={{ fontWeight: 600 }}>
                <Box>
                    <FormattedMessage id="app.bonusSettlements.supplierDetail.total" />
                </Box>
                <Box>
                    <FormattedNumber
                        value={PoolMath.round(total)}
                        style="currency"
                        currency={defaultCurrency}
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                    />
                </Box>
            </StyledSumTableRow>
        </Box>
    );
};
